import { useEffect } from "react";
import { FileText, BookOpen, LayoutGrid, Download, ExternalLink, Mail, Phone, Globe, ArrowRight, Flag, Star } from "lucide-react";
import { CONTACT } from "@/data/content";
import { api } from "@/lib/api";
import badge from "@/assets/golf-classic-badge-transparent.png";

/* Shareable Sponsor Kit — one link bundling every sponsor-facing document.
   Route: /sponsor-kit  (branded, standalone, not in public site nav) */
const NAVY = "#0e1e3a", GOLD = "#c9a24b", RED = "#b42230";

const DOCS = [
  {
    slug: "prospectus", icon: FileText,
    title: "Corporate Sponsorship Prospectus",
    kind: "Executive Packet · Multi-Page",
    desc: "The complete decision-maker's packet — mission, business value, all sponsorship levels, the full benefits matrix, and a commitment form.",
    view: "/prospectus", file: "/wayne-walker-sponsorship-prospectus.pdf",
  },
  {
    slug: "marketing", icon: BookOpen,
    title: "Marketing & Brand Partnership Guide",
    kind: "Inspirational Guide · 12 Pages",
    desc: "A story-driven introduction to who we are, our mission, Wayne Walker's legacy, and the meaningful value of partnership.",
    view: "/marketing-guide", file: "/wayne-walker-marketing-guide.pdf",
  },
  {
    slug: "flyers", icon: LayoutGrid,
    title: "Sponsorship Flyer Series",
    kind: "8 One-Page Flyers · Sales Suite",
    desc: "A coordinated set of premium one-pagers — Founding Sponsor, Why Sponsor, Opportunities, the Business Case, and more. Perfect for quick sharing.",
    view: "/sales-flyers", file: "/wwf-sponsorship-flyer-suite.pdf",
  },
];

export default function SponsorKit() {
  useEffect(() => {
    api.post("/track", { event: "kit_open" }).catch(() => {});
  }, []);

  const trackDownload = (slug) => {
    api.post("/track", { event: "download", doc: slug }).catch(() => {});
  };

  return (
    <div data-testid="sponsor-kit-page" style={{ background: NAVY, minHeight: "100vh", fontFamily: "'Helvetica Neue', Arial, sans-serif" }} className="text-white">
      <style>{`.serif{font-family:Georgia,'Times New Roman',serif}`}</style>

      {/* Hero */}
      <header className="relative overflow-hidden">
        <img src="/ff-flag-raise.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(160deg, ${NAVY} 35%, rgba(14,30,58,.82) 100%)` }} />
        <div className="relative max-w-4xl mx-auto px-6 sm:px-10 pt-14 pb-12 sm:pt-20 sm:pb-16 text-center">
          <img src={badge} alt="Heroes & Legacy Golf Classic" className="w-24 h-24 sm:w-28 sm:h-28 object-contain mx-auto" />
          <div className="mt-5 flex items-center justify-center gap-3">
            <span className="h-px w-10" style={{ background: RED }} /><Star size={13} style={{ color: RED }} /><span className="h-px w-10" style={{ background: RED }} />
          </div>
          <p className="mt-4 text-xs sm:text-sm tracking-[0.3em] uppercase" style={{ color: GOLD }}>Sponsor Kit · Inaugural 2027</p>
          <h1 className="serif font-bold leading-[1.05] mt-3 text-4xl sm:text-5xl">Everything You Need to<br /><span style={{ color: GOLD }}>Partner With Us</span></h1>
          <p className="mt-5 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed" style={{ color: "#c7d0e0" }}>
            Thank you for considering a partnership with the Wayne Walker Foundation and The Heroes &amp; Legacy Golf Classic.
            Below is our complete sponsorship kit — view any document online or download the PDF.
          </p>
        </div>
      </header>

      {/* Documents */}
      <main className="max-w-4xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <div className="grid gap-6">
          {DOCS.map((d) => (
            <article
              key={d.slug}
              data-testid={`kit-doc-${d.slug}`}
              className="rounded-sm border p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-7 transition-transform hover:-translate-y-0.5"
              style={{ background: "rgba(255,255,255,.04)", borderColor: "rgba(201,162,75,.28)" }}
            >
              <div className="shrink-0 w-14 h-14 flex items-center justify-center" style={{ background: RED }}>
                <d.icon size={26} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-[0.2em] uppercase" style={{ color: GOLD }}>{d.kind}</div>
                <h2 className="serif font-bold text-xl sm:text-2xl mt-1 leading-tight">{d.title}</h2>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "#c7d0e0" }}>{d.desc}</p>
              </div>
              <div className="flex sm:flex-col gap-3 shrink-0 w-full sm:w-auto">
                <a
                  data-testid={`kit-download-${d.slug}`}
                  href={d.file}
                  download
                  onClick={() => trackDownload(d.slug)}
                  className="flex-1 sm:flex-none justify-center text-xs font-semibold tracking-widest uppercase px-5 py-3 flex items-center gap-2 text-white transition-transform hover:-translate-y-0.5"
                  style={{ background: RED }}
                >
                  <Download size={14} /> Download
                </a>
                <a
                  data-testid={`kit-view-${d.slug}`}
                  href={d.view}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 sm:flex-none justify-center text-xs font-semibold tracking-widest uppercase px-5 py-3 flex items-center gap-2 border transition-colors hover:bg-white/5"
                  style={{ borderColor: "rgba(255,255,255,.35)", color: "#fff" }}
                >
                  <ExternalLink size={14} /> View
                </a>
              </div>
            </article>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 rounded-sm p-8 sm:p-10 text-center" style={{ background: "rgba(201,162,75,.08)", border: `1px solid rgba(201,162,75,.3)` }}>
          <Flag size={22} style={{ color: GOLD }} className="mx-auto" />
          <h3 className="serif font-bold text-2xl sm:text-3xl mt-4">Ready to Become a Founding Partner?</h3>
          <p className="mt-3 text-sm sm:text-base max-w-xl mx-auto" style={{ color: "#c7d0e0" }}>
            Reserve your sponsorship for the inaugural Classic and be recognized forever as one of the businesses that helped start it all.
          </p>
          <a
            data-testid="kit-cta-sponsor"
            href="https://hlgolfclassic.com/sponsor"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 mt-6 text-sm font-semibold tracking-widest uppercase px-7 py-4 text-white transition-transform hover:-translate-y-0.5"
            style={{ background: RED }}
          >
            Become a Sponsor <ArrowRight size={16} />
          </a>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-sm" style={{ color: "#e6ebf3" }}>
            <a href={`mailto:${CONTACT.sponsorEmail}`} className="flex items-center gap-2 hover:underline"><Mail size={14} style={{ color: GOLD }} /> {CONTACT.sponsorEmail}</a>
            <a href={`tel:${CONTACT.sponsorPhone}`} className="flex items-center gap-2 hover:underline"><Phone size={14} style={{ color: GOLD }} /> {CONTACT.sponsorPhone}</a>
            <span className="flex items-center gap-2"><Globe size={14} style={{ color: GOLD }} /> {CONTACT.website}</span>
          </div>
        </div>
      </main>

      <footer className="border-t py-7 text-center text-[11px] tracking-[0.22em] uppercase" style={{ borderColor: "rgba(255,255,255,.12)", color: "#9aa3b2" }}>
        Wayne Walker Foundation · The Heroes &amp; Legacy Golf Classic · Est. 2027
      </footer>
    </div>
  );
}
